<template>
  <div class="edit-scenario-custom">
    <div v-if="!Object.keys(structuredFields).length" class="flex flex-col">
      <EditScenarioFrameWorkSkeletonLoader />
    </div>
    <div v-else>
      <h3>{{ `Szenariorahmen "${scenarioFrame.name?.value}" bearbeiten` }}</h3>
      <v-card elevation="0" class="mt-3">
        <div class="border-b-2">
          <v-tabs
            v-model="tab"
            class="mb-[-2px]"
            density="compact"
            slider-color="#0692BC"
          >
            <v-tab :class="tabStyle" value="titel" :ripple="false"
              ><h5>Titel</h5></v-tab
            >
            <v-tab
              v-for="header in tabData"
              :key="header"
              :class="tabStyle"
              :value="header"
              :ripple="false"
              ><h5>{{ header }}</h5></v-tab
            >
          </v-tabs>
        </div>
        <v-card-text class="px-0">
          <v-window v-model="tab">
            <div v-if="tab === 'titel'">
              <TabTitle
                :label="'Szenariorahmen'"
                :scenario-frame-name="scenarioFrame.name"
                :read-only="readOnly"
                @update-name="scenarioFrame.name.value = $event"
              />
            </div>
            <TabDynamic
              v-if="tab !== 'titel'"
              :tab="tab"
              :data="getTabData(tab)"
              :read-only="readOnly"
              @shared-values-valid="sharedValuesValid = $event"
            />
          </v-window>
        </v-card-text>
        <!-- finish-->
        <div class="flex items-center justify-between gap-[20px]">
          <ButtonEl
            v-if="scenarioFrame.progress === 1"
            text="Für Szenarioberechnung freigeben"
            color="success"
            data-test="enable-for-scenario-computation"
            @click="releaseDialog = true"
          />
          <div class="ml-auto flex gap-5">
            <ButtonEl text="Abbrechen" variant="secondary" @click="abortForm" />
            <ButtonEl
              text="Speichern"
              :disabled="
                readOnly || !scenarioFrame.isValid || !sharedValuesValid
              "
              @click="saveFrame()"
            />
          </div>
        </div>
        <VuetifyDialog
          v-model="releaseDialog"
          :title="`Szenariorahmen ${scenarioFrame.name.value} freigeben?`"
        >
          <template #content>
            Nach Veröffentlichung des Szenariorahmens kann dieser verwendet
            werden,
            <br />
            um die Szenarioberechnung zu parametrisieren. Eine nachträgliche
            <br />
            Bearbeitung ist jedoch nicht mehr möglich.
          </template>
          <template #actions>
            <ButtonEl
              variant="secondary"
              text="Abbrechen"
              @click="releaseDialog = false"
            />
            <ButtonEl
              text="freigeben"
              variant="secondary"
              color="success"
              data-test="enable-for-scenario-computation-confirm"
              @click="releaseToEdit"
            />
          </template>
        </VuetifyDialog>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { axios } from '@/utils/axiosHelper';
import { computed, nextTick, ref, watch } from 'vue';
import ScenarioFrame from './scenario-frame';
import cookie from 'vue-cookies';
import EditScenarioFrameWorkSkeletonLoader from '@/features/scenario-frame/components/EditScenarioFrameWorkSkeletonLoader.vue';
import TabDynamic from '@/features/scenario-frame/components/TabDynamic.vue';
import ButtonEl from '@/components/storybook/src/stories/button/ButtonEl.vue';
import VuetifyDialog from '@/components/storybook/src/stories/vuetifyDialog/VuetifyDialog.vue';
import TabTitle from '@/features/scenario-frame/components/TabTitle.vue';

const emit = defineEmits(['abort', 'updateBaseData']);

const props = defineProps({ editScenarioId: Number });

const tab = ref('Allgemein');
const tabStyle =
  'ml-0 px-2 normal-case text-[18px] hover:text-infra-highlight-500 ml-4 tracking-normal';
const tabData = computed(() => Object.keys(structuredFields.value).sort());
const getTabData = computed(() => (tab) => structuredFields.value[tab]);
const readOnly = computed(() => scenarioFrame.value.progress === 0);
const releaseDialog = ref(false);
const scenarioFrame = ref(new ScenarioFrame());
const structuredFields = ref({});
const sharedValuesValid = ref(true);

function updateName(name) {
  scenarioFrame.value.name = name;
}

watch(
  () => props.editScenarioId,
  (newId) => {
    axios({ url: `/api/scenarioFrames/${newId}/`, method: 'GET' }).then(
      (resp) => {
        structuredFields.value = {};
        scenarioFrame.value.populateData(resp.data);
        const fields = scenarioFrame.value.fields.concat(
          scenarioFrame.value.yearly.fields,
          scenarioFrame.value.power.fields,
        );
        for (const field of fields) {
          if (!field.wildcard) continue;
          const tab = field.wildcard.tab;
          const exp = field.wildcard.expandable;
          if (!structuredFields.value[tab]) {
            structuredFields.value[tab] = {};
          }
          if (!structuredFields.value[tab][exp]) {
            structuredFields.value[tab][exp] = [];
          }
          structuredFields.value[tab][exp].push(field);
        }
      },
    );
  },
  { immediate: true },
);

function releaseToEdit() {
  scenarioFrame.value.fields.find((e) => e.key === 'progress').value = 0;
  axios({
    url: `/api/scenarioFrames/${scenarioFrame.value.id}/`,
    method: 'PATCH',
    data: { progress: 0 },
    headers: { 'X-CSRFToken': cookie.get('csrftoken') },
  }).then(() => {
    scenarioFrame.value.progress = 0;
    emit('updateBaseData');
    releaseDialog.value = false;
  });
}

function abortForm() {
  scenarioFrame.value = new ScenarioFrame();
  emit('abort', false);
  // scroll to top
  nextTick(() => {
    document.querySelector('.single-page-scroll-wrapper').scrollTo({
      top: 0,
    });
  });
}

function saveFrame() {
  const payload = scenarioFrame.value.payload;
  axios({
    url: `/api/scenarioFrames/${scenarioFrame.value.id}/`,
    method: 'PUT',
    data: payload,
    headers: { 'X-CSRFToken': cookie.get('csrftoken') },
  }).then(() => {
    emit('updateBaseData');
    abortForm();
  });
}
</script>

<style lang="scss">
.edit-scenario-custom {
  .v-window,
  .v-card {
    overflow: visible;
  }

  .v-tab {
    min-width: unset !important;

    h5 {
      @apply text-blue-grey-500;
    }

    &.v-slide-group-item--active h5 {
      color: #0692bc !important;
    }
  }
}
</style>
